import shp from 'shpjs'

import type { GeoPointFeature } from '../../../../../../../types'

/** Nomes de pontos extraídos de um arquivo importado (KML ou Shapefile). */
export interface SelecaoImportada {
    /** NOMEs normalizados (sem zero à esquerda) encontrados no arquivo. */
    nomes: string[]
    /** Total de pontos lidos do arquivo, antes de casar com a malha. */
    total: number
}

/** Normaliza o NOME para comparação: "07" e 7 viram "7". */
export function normalizarNome(valor: unknown): string {
    const texto = String(valor ?? '').trim()
    if (texto === '') return ''
    const numero = parseInt(texto, 10)
    return isNaN(numero) ? texto.toUpperCase() : String(numero)
}

/** Lê os <name> dos <Placemark> de um KML (formato gerado no download da análise). */
function lerNomesDoKml(conteudo: string): string[] {
    const doc = new DOMParser().parseFromString(conteudo, 'application/xml')

    if (doc.querySelector('parsererror')) {
        throw new Error('KML inválido')
    }

    const placemarks = Array.from(doc.getElementsByTagName('Placemark'))
    const nomes: string[] = []

    placemarks.forEach((placemark) => {
        // getElementsByTagName inclui descendentes; o <name> do Placemark é o primeiro.
        const nome = placemark.getElementsByTagName('name')[0]?.textContent
        const normalizado = normalizarNome(nome)
        if (normalizado !== '') nomes.push(normalizado)
    })

    return nomes
}

/** Lê os NOMEs das features de um Shapefile (.zip gerado no download da análise). */
async function lerNomesDoShape(arquivo: File): Promise<string[]> {
    const buffer = await arquivo.arrayBuffer()
    const geojson: any = await shp(buffer)

    // shpjs devolve uma FeatureCollection ou um array delas (zip com vários .shp).
    const colecoes = Array.isArray(geojson) ? geojson : [geojson]
    const nomes: string[] = []

    colecoes.forEach((colecao: any) => {
        ;(colecao?.features ?? []).forEach((feature: any) => {
            const props = feature?.properties ?? {}
            const normalizado = normalizarNome(props.NOME ?? props.INDICE ?? props.AUTO_ID)
            if (normalizado !== '') nomes.push(normalizado)
        })
    })

    return nomes
}

/**
 * Lê um KML (.kml) ou Shapefile (.zip) e devolve os NOMEs dos pontos contidos nele.
 *
 * Espelha o que o download da análise gera: o KML grava o NOME em <name> e o shape
 * na coluna NOME. Função pura — não toca em estado; lança em arquivo inválido.
 */
export async function lerSelecaoDoArquivo(arquivo: File): Promise<SelecaoImportada> {
    const nome_arquivo = (arquivo?.name ?? '').toLowerCase()

    let nomes: string[] = []

    if (nome_arquivo.endsWith('.kml')) {
        nomes = lerNomesDoKml(await arquivo.text())
    } else if (nome_arquivo.endsWith('.zip') || nome_arquivo.endsWith('.shp')) {
        nomes = await lerNomesDoShape(arquivo)
    } else {
        throw new Error('Formato não suportado. Envie um .kml ou o .zip do shape.')
    }

    // Remove repetidos preservando a ordem de leitura.
    const unicos = Array.from(new Set(nomes))

    return { nomes: unicos, total: unicos.length }
}

/**
 * Casa os NOMEs importados com os pontos da malha atual.
 * Pontos do arquivo que não existem na malha são ignorados e contabilizados.
 */
export function casarComMalha(nomes: string[], pontos_da_malha: GeoPointFeature[]) {
    const procurados = new Set(nomes)

    const encontrados = (pontos_da_malha ?? []).filter((ponto) =>
        procurados.has(normalizarNome(ponto?.NOME))
    )

    const nomes_na_malha = new Set((pontos_da_malha ?? []).map((p) => normalizarNome(p?.NOME)))
    const ignorados = nomes.filter((nome) => !nomes_na_malha.has(nome))

    return { encontrados, ignorados }
}
